import { Menu, ActionIcon } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { isWeb } from '../utils/environment';
import { emit } from '@tauri-apps/api/event';

interface LanguageSelectorProps {
  onLanguageChange?: (language: string) => void;
}

export function LanguageSelector({ onLanguageChange }: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'en');

  useEffect(() => {
    // Sync with i18n language if it changes externally
    if (i18n.language && i18n.language !== currentLanguage) {
      setCurrentLanguage(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    // Load language from backend settings if user is logged in
    const loadUserLanguage = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await apiService.getSettings();
          const settings = response.settings || {};
          if (settings.language && settings.language !== currentLanguage) {
            setCurrentLanguage(settings.language);
            await i18n.changeLanguage(settings.language).catch(() => undefined);
          }
        }
      } catch (error) {
        console.error('Failed to load language setting:', error);
      }
    };
    
    loadUserLanguage();
  }, []);

  const languages = [
    { value: 'en', label: t('languageSelector.languages.english') },
    { value: 'ar', label: t('languageSelector.languages.arabic') },
    { value: 'nl', label: t('languageSelector.languages.dutch') },
    { value: 'fr', label: t('languageSelector.languages.french') },
    { value: 'es', label: t('languageSelector.languages.spanish') },
    { value: 'de', label: t('languageSelector.languages.german') },
    { value: 'it', label: t('languageSelector.languages.italian') },
    { value: 'ru', label: t('languageSelector.languages.russian') },
    { value: 'tr', label: t('languageSelector.languages.turkish') },
  ];

  const changeLanguage = async (lng: string) => {
    // Update i18n language immediately
    await i18n.changeLanguage(lng).catch(() => undefined);
    setCurrentLanguage(lng);
    
    // Dispatch custom event for other components to listen to (same window)
    window.dispatchEvent(new CustomEvent('language-changed', { detail: { language: lng } }));

    // Emit Tauri event for cross-window communication (for correction popup) - only in Tauri
    if (!isWeb()) {
      try {
        await emit('language-changed', lng);
      } catch (error) {
        console.error('Failed to emit language change event:', error);
      }
    }
    
    // Try to save to backend if user is logged in
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Get current settings
        const response = await apiService.getSettings();
        const currentSettings = response.settings || {};
        
        // Update language in settings
        const updatedSettings = {
          ...currentSettings,
          language: lng
        };
        
        await apiService.updateSettings(updatedSettings);
        
        // Notify parent component if callback provided
        if (onLanguageChange) {
          onLanguageChange(lng);
        }
      } else {
        // Not logged in, just save to localStorage
        localStorage.setItem('kalimat-language', lng);
      }
    } catch (error) {
      console.error('Failed to save language setting:', error);
      // Still update UI even if backend save fails
      localStorage.setItem('kalimat-language', lng);
    }
  };

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon
          variant="outline"
          color="blue"
          size="lg"
          title="Select language"
          aria-label="Select language"
        >
          <IconLanguage size={18} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{t('languageSelector.title')}</Menu.Label>
        <Menu.Divider />
        {languages.map((lang) => (
          <Menu.Item
            key={lang.value}
            onClick={() => changeLanguage(lang.value)}
            style={{
              backgroundColor: i18n.language === lang.value ? 'var(--mantine-color-blue-light)' : 'transparent',
              color: i18n.language === lang.value ? 'var(--mantine-color-blue-filled)' : 'inherit'
            }}
          >
            {lang.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
